import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useState, useRef } from 'react';
import { FormikProps } from 'formik';
import { useStore } from '../../stores/store';
import ParcelParticipantPersonalFormDynamic from '../../features/parcel-creation/parcel-participant-personal/parcel-participant-personal-form';
import ParcelParticipantContactForm from '../../features/parcel-creation/parcel-participant-contact/parcel-participant-contact.component';
import ParcelParticipantPudoForm from '../../features/parcel-creation/parcel-participant-pudo/parcel-participant-pudo.component';
import DeliveryDetailsForm, {
  UserRole,
} from '../../features/parcel-creation/delivery-details-form/delivery-details-form.component';
import { removeCountryCallingCodeFromPhone } from '../personal-data/contact-details-form.component';
import { getSettingReceiverCountry } from '../../utils/countries/countries.utils';
import { Address, Recipient } from '../../models/parcelDeliveryMiles';
import { toddmmYYYY } from '../../utils/generic/generic.utils';
import { isFeatureRequired } from '../../utils/client/client.utils';
import ParcelReceiverContactsSidebar from './parcel-receiver-contacts-sidebar.component';

export interface IContactDetailsFormValues {
  phone: string;
  phone_country_code: string;
  email: string;
}

export interface IPersonalDataFormValues {
  first_name: string;
  last_name: string;
}

const ParcelReceiver = () => {
  const {
    parcelCreationStore: {
      shipment,
      selectedReceiverPudo,
      setRecipientPersonalDataFormValues,
      setIsReceiverPersonalDataValid,
      setReceiverPhone,
      setReceiverPhoneCountryCode,
      setReceiverEmail,
      setReceiverPudo,
      getReceiverDetails,
      countryDestination,
      countryDeparture,
      navigation,
      isDomestic,
      getContactDetailsValidationSchema,
    },
    shipmentCountriesStore: { receiverCountries },
    navStore: { toggleSidebarByName },
  } = useStore();

  const [selectedOption, setSelectedOption] = useState<Recipient | undefined>();

  const { t } = useTranslation();

  const isDefaultFlow =
    getSettingReceiverCountry(countryDeparture?.code, isDomestic) ===
    'default_flow';

  const formikRefPersonal = useRef<FormikProps<
    Record<string, string | null>
  > | null>(null);
  const formikRefContact =
    useRef<FormikProps<IContactDetailsFormValues> | null>(null);
  const formikRefAddress = useRef<FormikProps<Address> | null>(null);

  if (!shipment) return null;

  const handleSearchOptionSelected = async (option: Recipient) => {
    const receiverData = await getReceiverDetails(option.id);
    if (!receiverData) return;
    setReceiverPhone(option.phone.phone_number ?? '');
    setReceiverPhoneCountryCode(option.phone.country_code ?? '');
    setReceiverEmail(option.email);

    if (formikRefPersonal?.current && receiverData.length > 0) {
      const formInputNames = Object.keys(
        formikRefPersonal.current.initialValues
      );

      const newValues = formInputNames.reduce((acc, inputName) => {
        const foundReceiverValue = receiverData.find(
          (x) => typeof x.name === 'string' && x.name.endsWith(inputName)
        );

        const viewBlock = navigation.current?.elements.find(
          (elem) => elem.type === 'view_block'
        );

        const inputDateFormFieldNames =
          viewBlock?.elements
            ?.filter((x) => x.type === 'input-date' && x.behavior?.form_field)
            .map((x) => x.behavior!.form_field as string) ?? [];

        const normalizedValue =
          foundReceiverValue?.value &&
          inputDateFormFieldNames.includes(inputName)
            ? toddmmYYYY(foundReceiverValue.value.toString())
            : foundReceiverValue?.value;

        return normalizedValue ? { ...acc, [inputName]: normalizedValue } : acc;
      }, {});

      formikRefPersonal.current.setValues(newValues);
    }

    if (formikRefContact?.current) {
      const phoneWithoutCountryCallingCode =
        removeCountryCallingCodeFromPhone(
          option.phone.phone_number ?? '',
          option.phone.country_code ?? ''
        ) ?? '';

      formikRefContact.current.setValues({
        phone: phoneWithoutCountryCallingCode,
        email: option.email,
        phone_country_code: option.phone.country_code ?? '',
      });
    }

    if (!formikRefAddress.current) {
      toggleSidebarByName('receiver_first_name');
      setSelectedOption(option);
      return;
    }

    const isCourierAndMatchingRegion =
      option.carrier.delivery_service.service_type.toLowerCase() ===
        'courier' &&
      (option?.address?.region === countryDestination?.region ||
        countryDestination?.region === undefined) &&
      option?.address?.post_code === countryDestination?.zipCode;

    const isCourierWithCustomFlow =
      option.carrier.delivery_service.service_type.toLowerCase() ===
        'courier' && !isDefaultFlow;

    if (isCourierAndMatchingRegion || isCourierWithCustomFlow) {
      formikRefAddress.current?.setValues({
        ...option.address,
        country: formikRefAddress.current.values.country,
      });
    } else {
      formikRefAddress.current?.setValues({
        full_address: '',
        post_code: '',
        region: '',
        city: '',
        street: '',
        building: '',
        section: '',
        apartment: '',
        buzz_code: '',
        note: '',
        country: formikRefAddress.current.values.country,
      });
    }

    toggleSidebarByName('receiver_first_name');
    setSelectedOption(option);
  };

  const isPudo = navigation.current?.name === 'receiver-details-pudo';

  const shouldBlockPhone = isFeatureRequired(
    'recipientPhoneNumberBlocked',
    countryDeparture?.code
  );

  if (!countryDestination) return;

  const allowedPhoneCountries = shouldBlockPhone
    ? [countryDestination]
    : receiverCountries;

  return (
    <>
      <ParcelParticipantPersonalFormDynamic
        key='recipient_personal_form'
        isReceiver
        innerRef={formikRefPersonal}
        setGlobalStateFormValues={setRecipientPersonalDataFormValues}
        setGlobalStateFormIsValid={setIsReceiverPersonalDataValid}
      />

      <ParcelParticipantContactForm
        validationSchema={getContactDetailsValidationSchema('receiver')}
        initialValues={{
          phone:
            removeCountryCallingCodeFromPhone(
              shipment.recipient?.phone?.phone_number ?? '',
              shipment.recipient?.phone?.country_code ?? ''
            ) ?? '',
          phone_country_code: countryDestination?.code ?? 'US',
          email: shipment.recipient?.email ?? '',
        }}
        storeParticipantPhone={setReceiverPhone}
        storeParticipantPhoneCountryCode={setReceiverPhoneCountryCode}
        storeParticipantEmail={setReceiverEmail}
        isDropdownDisabled={shouldBlockPhone}
        innerRef={formikRefContact}
        allowedPhoneCountries={allowedPhoneCountries}
        defaultCountryCode={countryDestination?.code}
      />

      {isPudo ? (
        <ParcelParticipantPudoForm
          initialValues={{
            pudo: selectedReceiverPudo,
          }}
          role={UserRole.receiver}
          header={t('drop_off_address', {
            carrierName: shipment.recipient?.carrier.delivery_carrier_name,
          })}
          storeRecipientPudo={setReceiverPudo}
        />
      ) : (
        <DeliveryDetailsForm
          role={UserRole.receiver}
          innerRef={formikRefAddress}
        />
      )}

      <ParcelReceiverContactsSidebar
        selectedOption={selectedOption}
        onSelect={handleSearchOptionSelected}
      />
    </>
  );
};

export default observer(ParcelReceiver);
